import { Activity, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router";
import { useBackDepthReport } from "./api/nativeBack.js";
import { ExitConfirm } from "./components/ExitConfirm.js";
import { TvStart, tvStartPending } from "./startup/TvStart.js";
import { SleepGuard } from "./player/SleepGuard.js";
import { PairRequestDialog } from "./remote/PairRequestDialog.js";
import { UpgradePrompt } from "./components/UpgradePrompt.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PlayerProvider } from "./player/PlayerProvider.js";
import { NavRail } from "./components/NavRail.js";
import { SyncScheduler } from "./components/SyncScheduler.js";
import { CatalogRebuildNotice } from "./components/CatalogRebuildNotice.js";
import { NotificationEngine } from "./components/NotificationEngine.js";
import { TopBar } from "./components/TopBar.js";
import { TvKeyboard } from "./components/TvKeyboard.js";
import { installSpatialNavigation } from "./components/spatialNav.js";
import { recordRoute, startPerfLog } from "./library/perfLog.js";
import { HomePage } from "./pages/HomePage.js";
import { RecordingsPage } from "./pages/RecordingsPage.js";
import { CatchupPage } from "./pages/CatchupPage.js";
import { GuideSettingsPage } from "./pages/GuideSettingsPage.js";
import { RemoteSettingsPage } from "./pages/RemoteSettingsPage.js";
import { AboutPage } from "./pages/AboutPage.js";
import { HelpPage } from "./pages/HelpPage.js";
import { ImageCreditsPage } from "./pages/ImageCreditsPage.js";
import { SocialPage } from "./pages/SocialPage.js";
import { installRemoteDispatch } from "./remote/remoteDispatch.js";
import { useRemoteAppActions } from "./remote/useRemoteAppActions.js";
import { PhoneRemoteBridge } from "./remote/PhoneRemoteBridge.js";
import { RemoteNote } from "./remote/RemoteNoteView.js";
import { LivePage } from "./pages/LivePage.js";
import { MoviesPage } from "./pages/MoviesPage.js";
import { SeriesPage } from "./pages/SeriesPage.js";
import { MyListPage } from "./pages/MyListPage.js";
import { SearchPage } from "./pages/SearchPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { SourcePage } from "./pages/SourcePage.js";
import { ParentalPage } from "./pages/ParentalPage.js";
import { ProfilesPage } from "./pages/ProfilesPage.js";
import { providerHasContent, useSettings } from "./settings/settingsStore.js";
import { useDvrSourceIdentity } from "./dvr/dvrSync.js";

// Exported for test cleanup: react-query's default gcTime keeps timers alive
// after unmount, which blocks the Node event loop in tests.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 5,
    },
  },
});

/**
 * Section 6 shell: the PlayerProvider sits ABOVE the router (technical-spec
 * §6.1) so the single Player instance and its dock survive every navigation.
 * The design follows the approved design-preview: dark cinematic surfaces,
 * cinema-red accent, NavRail navigation.
 */
function Shell() {
  const location = useLocation();
  // Home is kept once visited: hidden while another page shows, not destroyed.
  // Rebuilding it was most of the ~2 s Back from the guide took on the 1 GB
  // projector (2026-09-24) — every rail, the Live Now matching, the storage
  // reads — and Home is where nearly every Back lands. <Activity> keeps its
  // state and DOM but stops its effects while hidden (timers, key handlers,
  // subscriptions), so a hidden Home costs memory and nothing else. Only Home:
  // the guide is the heavy page and is still rebuilt, by choice.
  const onHome = location.pathname === "/";
  // A cold start that will jump to the guide (Settings -> Home -> Start on: Live TV) never
  // mounts Home first: its heavy first render held Home on screen and delayed the jump.
  // TvStart, below, makes the jump in the first effect and Home mounts when it is reached.
  const leavingHome = tvStartPending(location.pathname);
  const [homeKept, setHomeKept] = useState(onHome && !leavingHome);
  if (onHome && !homeKept && !leavingHome) setHomeKept(true);
  // The APK decides Back in Java and cannot ask the page a question, so the
  // page keeps it told how deep its own Back stack is (api/nativeBack.ts).
  useBackDepthReport(location.key);
  // Android app: the DVR follows the active source the moment it changes.
  useDvrSourceIdentity();
  // The Live TV page has its own single-row toolbar — no global search there.
  const hideTopBar = location.pathname.startsWith("/live");
  // D-pad navigation everywhere a screen does not handle the arrows itself —
  // the only way around the app on a TV remote (components/spatialNav.ts).
  // Remote buttons the viewer mapped (Settings → Remote control). Installed
  // BEFORE the arrows' navigation below, so a mapped button is heard first.
  useEffect(() => installRemoteDispatch(), []);
  useRemoteAppActions();
  useEffect(() => installSpatialNavigation(), []);
  // The flight recorder: long tasks, heap, and how long each screen took, kept
  // in memory for remote debugging's /perf to read after a freeze.
  useEffect(() => startPerfLog(), []);
  useEffect(() => {
    const started = performance.now();
    const frame = requestAnimationFrame(() => recordRoute(location.pathname, performance.now() - started));
    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);
  // First-run gate. This build never falls back to an owner/platform provider
  // (docs/architecture-decisions.md, "The PUBLIC deployment is Model A"), so
  // with no provider on the device every screen would be an empty shell and
  // every request would answer provider_not_configured. Show the provider
  // setup instead, and nothing else — no NavRail, no TopBar, and crucially no
  // SyncScheduler or NotificationEngine, which would otherwise poll the API on
  // a loop for a provider that does not exist.
  //
  // Reuses SourcePage verbatim rather than duplicating a form, so validation,
  // multi-provider support and remote/keyboard focus behave identically here
  // and in Settings. The gate lifts by itself: addProvider() activates the
  // first provider it stores, so `activeProvider` becomes defined and this
  // component re-renders into the real app.
  // First-run only. Two ways in:
  //   - no sources at all (fresh device, or the user removed the last one)
  //   - never onboarded, and the current source has not validated with at
  //     least MIN_PROVIDER_CONTENT items
  // Once ANY source has qualified, setupCompleted latches and the user goes
  // straight to Home from then on, managing sources in Settings. A lapsed
  // subscription therefore shows errors in place rather than throwing someone
  // who already got in back to a full-screen setup wall.
  const needsProvider = useSettings(
    (s) => s.providers.length === 0 || (!s.setupCompleted && !providerHasContent(s.activeProvider)),
  );
  if (needsProvider) {
    return (
      <div className="app app--onboarding">
        <main className="app__content">
          <SourcePage />
        </main>
        {/* Setup is where a viewer types a URL and a password, so the built-in
            keyboard has to be here too — this branch returns before the shell
            below it ever renders (components/TvKeyboard.tsx). */}
        <TvKeyboard />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Section 12: background sync (EPG/VOD/Series refresh + health probe)
          and notifications (starting-soon + tracked titles). Both render
          nothing and live above the routes so they run on every screen. */}
      <SyncScheduler />
      <NotificationEngine />
      {/* Phones paired in Settings → Phone remote: their actions and the second screen. */}
      <PhoneRemoteBridge />
      {/* A phone asking to pair without a code: "Allow … to control this TV?" */}
      <PairRequestDialog />
      {/* The top bar shows it beside the bell; the guide has no top bar. */}
      {hideTopBar && <CatalogRebuildNotice />}
      {/* Above the routes so the one press that ends the session is guarded
          from every screen, not only the one that happens to be mounted. */}
      <ExitConfirm />
      {/* A profile that chose "Start on: Live TV" jumps to the guide once per cold start. */}
      <TvStart />
      {/* The sleep timer and "Still watching?": asks, and stops playback, from any screen. */}
      <SleepGuard />
      {/* Free viewing time used up: ask to upgrade, whatever screen hit it. */}
      <UpgradePrompt />
      {/* The built-in keyboard, for devices whose own keyboard cannot be driven
          by a remote. Renders nothing until such a device is detected. */}
      <TvKeyboard />
      <NavRail />
      <div className="app__main">
        {!hideTopBar && <TopBar />}
        <main className="app__content">
          {homeKept && (
            <Activity mode={onHome ? "visible" : "hidden"}>
              <HomePage />
            </Activity>
          )}
          <Routes>
            {/* Home itself is rendered above, kept alive; the route stays so
                "/" is a known path rather than the catch-all below. */}
            <Route path="/" element={null} />
            <Route path="/live" element={<LivePage />} />
            <Route path="/movies" element={<MoviesPage />} />
            <Route path="/series" element={<SeriesPage />} />
            <Route path="/mylist" element={<MyListPage />} />
            <Route path="/catchup" element={<CatchupPage />} />
            <Route path="/recordings" element={<RecordingsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/social" element={<SocialPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/guide" element={<GuideSettingsPage />} />
            <Route path="/settings/source" element={<SourcePage />} />
            <Route path="/settings/parental" element={<ParentalPage />} />
            <Route path="/settings/remote" element={<RemoteSettingsPage />} />
            <Route path="/settings/help" element={<HelpPage />} />
            <Route path="/settings/about" element={<AboutPage />} />
            <Route path="/settings/about/images" element={<ImageCreditsPage />} />
            <Route path="/settings/profiles" element={<ProfilesPage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </main>
      </div>
      <RemoteNote />
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PlayerProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </PlayerProvider>
    </QueryClientProvider>
  );
}
